import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Drawer, DrawerProps } from 'antd';

export type DrawerContextValue = {
  openDrawer: (renderFn: DrawerRenderFunction, options?: DrawerOptions) => void;
};
export const DrawerContext = createContext<DrawerContextValue>({
  openDrawer: () => {},
});

export type DrawerRenderFunction = (params: {
  closeDrawer: () => void;
}) => ReactNode;

type DrawerOptions = DrawerProps;

const defaultOptions = {
  placement: 'bottom',
  width: '100%',
} as DrawerOptions;

const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState<ReactNode>(null);
  const [options, setOptions] = useState<DrawerOptions>(defaultOptions);
  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const contextValue = useMemo(() => {
    return {
      openDrawer: (renderFn: DrawerRenderFunction, options?: DrawerOptions) => {
        setOptions(options ?? defaultOptions);
        setDrawer(renderFn({ closeDrawer }));
        setOpen(true);
      },
    };
  }, []);

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}
      <Drawer
        closable={false}
        onClose={closeDrawer}
        open={open}
        destroyOnClose={true}
        {...options}
      >
        {drawer}
      </Drawer>
    </DrawerContext.Provider>
  );
};

export default DrawerProvider;
